// API client for Valentine Farm Game

// In development, if accessing via Vite (5173), point API to Wrangler (8788)
const isVite = typeof window !== 'undefined' && window.location.port === '5173';
const API_BASE = isVite ? 'http://localhost:8788/api' : '/api';

console.log('API Client initialized. Base:', API_BASE);

interface SessionResponse {
    sessionId: string;
    publicToken: string;
    createdAt: number;
}

interface SessionData {
    sessionId: string;
    publicToken: string;
    createdAt: number;
    answers: Record<string, string>;
}

interface AnswerResponse {
    success: boolean;
    questionKey: string;
    answerValue: string;
    createdAt: number;
}

interface PublicSummary {
    createdAt: number;
    answers: Record<string, string>;
    townState: TownState;
}

export interface TownState {
    buildings: Array<{ slot: string; type: string; level: number }>;
    decorations: string[];
    tier: 'basic' | 'premium';
}

interface ApiError {
    error: {
        code: string;
        message: string;
    };
}

// Storage keys
const STORAGE_KEYS = {
    SESSION_ID: 'valentine_session_id',
    PUBLIC_TOKEN: 'valentine_public_token',
};

// Create a new session
export async function createSession(): Promise<SessionResponse> {
    const response = await fetch(`${API_BASE}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
        const error = (await response.json()) as ApiError;
        throw new Error(error.error.message);
    }

    const data = (await response.json()) as SessionResponse;

    // Store in localStorage
    localStorage.setItem(STORAGE_KEYS.SESSION_ID, data.sessionId);
    localStorage.setItem(STORAGE_KEYS.PUBLIC_TOKEN, data.publicToken);

    return data;
}

// Get stored session ID
export function getStoredSessionId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.SESSION_ID);
}

// Get stored public token
export function getStoredPublicToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.PUBLIC_TOKEN);
}

// Rehydrate session from server
export async function rehydrateSession(sessionId: string): Promise<SessionData> {
    const response = await fetch(`${API_BASE}/session/${sessionId}`);

    if (!response.ok) {
        const error = (await response.json()) as ApiError;
        throw new Error(error.error.message);
    }

    return (await response.json()) as SessionData;
}

// Save an answer
export async function saveAnswer(
    sessionId: string,
    questionKey: string,
    answerValue: string
): Promise<AnswerResponse> {
    const response = await fetch(`${API_BASE}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, questionKey, answerValue }),
    });

    if (!response.ok) {
        const error = (await response.json()) as ApiError;
        throw new Error(error.error.message);
    }

    return (await response.json()) as AnswerResponse;
}

// Get public summary by token
export async function getPublicSummary(publicToken: string): Promise<PublicSummary> {
    const response = await fetch(`${API_BASE}/public/${publicToken}`);

    if (!response.ok) {
        const error = (await response.json()) as ApiError;
        throw new Error(error.error.message);
    }

    return (await response.json()) as PublicSummary;
}

// Clear stored session (for testing)
export function clearSession(): void {
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    localStorage.removeItem(STORAGE_KEYS.PUBLIC_TOKEN);
}

// Generate share URL
export function getShareUrl(publicToken: string): string {
    const url = new URL(window.location.href);
    url.pathname = '/';
    url.searchParams.set('share', publicToken);
    return url.toString();
}

// Copy share URL to clipboard
export async function copyShareUrl(publicToken: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(getShareUrl(publicToken));
        return true;
    } catch {
        return false;
    }
}
